class ProductDetailsTabs extends HTMLElement {
  constructor() {
    super();
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.indicator = this.querySelector('.product-details-tabs__indicator');
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab, false));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });

    this.querySelectorAll('size-guide').forEach((guide) => this.initUnitToggle(guide));

    // "Size guide" links live outside this element (in the product form).
    document.addEventListener('click', this.onTriggerClick);
    window.addEventListener('resize', this.onResize);

    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.positionIndicator(active, false);

    // Re-select the active tab when a block is chosen in the theme editor.
    document.addEventListener('shopify:block:select', (event) => {
      const tab = this.tabs.find((t) => t.contains(event.target) || t === event.target);
      if (tab) this.activate(tab, false);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    window.removeEventListener('resize', this.onResize);
  }

  activate(tab, setFocus = true) {
    if (!tab) return;
    this.tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.setAttribute('tabindex', selected ? '0' : '-1');
      t.classList.toggle('is-active', selected);
    });

    this.panels.forEach((panel) => {
      const show = panel.id === tab.getAttribute('aria-controls');
      panel.classList.toggle('is-active', show);
      if (show) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.positionIndicator(tab, true);
    if (setFocus) tab.focus();
  }

  positionIndicator(tab, animate) {
    if (!this.indicator || !tab) return;
    const apply = () => {
      if (!animate) this.indicator.style.transition = 'none';
      this.indicator.style.width = `${tab.offsetWidth}px`;
      this.indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
      if (!animate) {
        // Force reflow, then restore the CSS transition.
        void this.indicator.offsetWidth;
        this.indicator.style.transition = '';
      }
    };
    requestAnimationFrame(apply);
  }

  onResize() {
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    this.positionIndicator(active, false);
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(document.activeElement);
    if (index === -1) return;
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    this.activate(next, true);
  }

  initUnitToggle(guide) {
    const buttons = Array.from(guide.querySelectorAll('.size-guide__unit'));
    const tables = Array.from(guide.querySelectorAll('[data-unit-table]'));
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const unit = button.dataset.unit;
        buttons.forEach((b) => {
          const on = b === button;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        tables.forEach((table) => {
          table.toggleAttribute('hidden', table.dataset.unitTable !== unit);
        });
      });
    });
  }

  onTriggerClick(event) {
    // "Size guide" opens the size guide; "Shipping details" under the delivery
    // estimate opens whichever tab holds the [delivery] token.
    const trigger = event.target.closest('[data-size-guide-trigger], [data-delivery-trigger]');
    if (!trigger) return;
    const panel = this.querySelector(
      trigger.hasAttribute('data-size-guide-trigger') ? '[data-size-guide]' : '[data-delivery-panel]'
    );
    if (!panel) return;
    event.preventDefault();
    const tab = this.tabs.find((t) => t.getAttribute('aria-controls') === panel.id);
    if (tab) this.activate(tab, false);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('product-details-tabs')) {
  customElements.define('product-details-tabs', ProductDetailsTabs);
}
